import * as _ from 'underscore'

import { ColumnModel, FieldModel } from "../api/api.models"

import { ConvertHelper, ToastHelper, LogHelper, IUserSession, LocalizationHelper } from '../../helpers'

import { Enums } from '../../models/common'
import { OpTaskType, OpData } from '../../models/core'
import { IOpObject, IOpData, IReferenceType } from '../../models/util/interfaces'
import { OpDataList } from '../../models/util'

import { CreateEditInitParams } from '../common/create-edit-popup/create-edit-popup.controller'
import { DetailsOptions } from '../common/details-popup/details-popup.directive'

export class TaskTypeHelper {

    // Consts

    private static get NAME_DV_KEY(): string { return 'Name' }
    private static get DURATION_ESTIMATE_DV_KEY(): string { return 'DurationEstimate' }
    private static get CODE_DV_KEY(): string { return 'Code' }
    private static get COLOR_DV_KEY(): string { return 'Color' }
    private static get TASK_COUNT_DV_KEY(): string { return 'TaskCount' }
    private static get ID_TASK_TYPE_GROUP_DV_KEY(): string { return 'IdTaskTypeGroup' }

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.TaskType]' }
    public static get FILE_NAME_PREFIX(): string { return 'tasks-types' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdTaskType }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Task_Type }

    // Consts End


    // Methods

    static deleteTaskType(userSession: IUserSession, taskType: OpTaskType): JQueryPromise<void> {
        const toDelete: OpTaskType = new OpTaskType(null)

        toDelete.idTaskType = taskType.idTaskType
        toDelete.opState = Enums.OpChangeState.Delete
        toDelete.creationDate = taskType.creationDate

        return userSession
            .getDataProvider().TaskType
            .deleteTaskType(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, TaskTypeHelper.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, TaskTypeHelper.SIMAX_MODULE_NAME))
                LogHelper.warn(TaskTypeHelper.LOG_PREFIX, `Unable to delete task type for id ${taskType.idTaskType}.`, error)
            })
    }

    public static getTaskType(userSession: IUserSession, idTaskType: number): JQueryPromise<OpTaskType> {
        return userSession
            .getDataProvider().TaskType
            .getTaskType(idTaskType)
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(TaskTypeHelper.LOG_PREFIX, `Unable to load TaskType for id ${idTaskType}`, error)

                return null
            })
    }

    public static saveTaskType(userSession: IUserSession, taskType: OpTaskType): JQueryPromise<OpTaskType> {
        return userSession
            .getDataProvider().TaskType
            .saveTaskType(taskType)
            .then((savedObject: OpTaskType) => {
                return userSession
                    .getDataProvider().System
                    .getDictionaries([Enums.ReferenceType.IdTaskType], true)
                    .then(() => {
                        return savedObject
                    })
            })
            .then((savedObject: OpTaskType) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, TaskTypeHelper.SIMAX_MODULE_NAME))

                return savedObject
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, TaskTypeHelper.SIMAX_MODULE_NAME))
                LogHelper.warn(TaskTypeHelper.LOG_PREFIX, `Unable to save task type for id ${taskType.idTaskType}.`, error)
            })

    }

    // Methods End


    // OpTaskType

    public static createOpTaskTypeFromIOpObject(sourceIOpObject: IOpObject, sourceTaskType: OpTaskType = null): OpTaskType {
        if (sourceIOpObject == null) {
            return
        }

        const taskType: OpTaskType = new OpTaskType(null)

        taskType.idTaskType = sourceIOpObject.idObject
        taskType.opState = sourceIOpObject.opState

        if (taskType.idTaskType === 0) {
            taskType.creationDate = new Date()
        }
        else {
            if (sourceTaskType != null) {
                taskType.creationDate = sourceTaskType.creationDate
            }
        }

        const nameData: IOpData = _.find(sourceIOpObject.dataList.toArray(), (opData: IOpData) => opData.idDataType === Enums.DataType.HELPER_NAME)
        const durationEstimateData: IOpData = _.find(sourceIOpObject.dataList.toArray(), (opData: IOpData) => opData.idDataType === Enums.DataType.HELPER_DURATION_ESTIMATE)
        const colorData: IOpData = _.find(sourceIOpObject.dataList.toArray(), (opData: IOpData) => opData.idDataType === Enums.DataType.HELPER_COLOR)
        const codeData: IOpData = _.find(sourceIOpObject.dataList.toArray(), (opData: IOpData) => opData.idDataType === Enums.DataType.HELPER_CODE)
        const taskGroupData: IOpData = _.find(sourceIOpObject.dataList.toArray(), (opData: IOpData) => opData.idDataType === Enums.DataType.HELPER_ID_TASK_TYPE_GROUP)

        if (nameData != null) {
            taskType.dynamicValues.push({
                Key: TaskTypeHelper.NAME_DV_KEY,
                Value: nameData.value
            })
        }

        if (durationEstimateData != null) {
            // bo create-edit zwraca liczby w stringach
            const durationEstimate = _.isFinite(durationEstimateData.value) ? Number(durationEstimateData.value) : null

            taskType.dynamicValues.push({
                Key: TaskTypeHelper.DURATION_ESTIMATE_DV_KEY,
                Value: durationEstimate
            })
        }

        if (colorData != null) {
            taskType.dynamicValues.push({
                Key: TaskTypeHelper.COLOR_DV_KEY,
                Value: colorData.value
            })
        }

        if (codeData != null) {
            taskType.dynamicValues.push({
                Key: TaskTypeHelper.CODE_DV_KEY,
                Value: codeData.value
            })
        }

        if (taskGroupData != null) {
            taskType.dynamicValues.push({
                Key: TaskTypeHelper.ID_TASK_TYPE_GROUP_DV_KEY,
                Value: taskGroupData.value
            })
        }

        return taskType
    }

    public static createIOpObjectFromOpTaskType(taskType: OpTaskType): IOpObject & IReferenceType {
        if (taskType == null) {
            return null
        }

        const nameDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.NAME_DV_KEY))
        const durationEstimateDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.DURATION_ESTIMATE_DV_KEY))
        const codeDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.CODE_DV_KEY))
        const colorDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.COLOR_DV_KEY))

        const dataList: OpDataList<IOpData> = new OpDataList<IOpData>()

        const nameData: IOpData = new OpData({
            ID_DATA_TYPE: Enums.DataType.HELPER_NAME,
            INDEX_NBR: 0,
            VALUE: nameDynamicValue.Value,
            OpState: Enums.OpChangeState.Loaded
        })

        const durationEstimateData: IOpData = new OpData({
            ID_DATA_TYPE: Enums.DataType.HELPER_DURATION_ESTIMATE,
            INDEX_NBR: 0,
            VALUE: durationEstimateDynamicValue.Value,
            OpState: Enums.OpChangeState.Loaded
        })

        const codeData: IOpData = new OpData({
            ID_DATA_TYPE: Enums.DataType.HELPER_CODE,
            INDEX_NBR: 0,
            VALUE: codeDynamicValue.Value,
            OpState: Enums.OpChangeState.Loaded
        })

        const colorData: IOpData = new OpData({
            ID_DATA_TYPE: Enums.DataType.HELPER_COLOR,
            INDEX_NBR: 0,
            VALUE: colorDynamicValue.Value,
            OpState: Enums.OpChangeState.Loaded
        })

        const idTaskTypeGroupData: IOpData = new OpData({
            ID_DATA_TYPE: Enums.DataType.HELPER_ID_TASK_TYPE_GROUP,
            INDEX_NBR: 0,
            VALUE: taskType.idTaskTypeGroup,
            OpState: Enums.OpChangeState.Loaded
        })

        dataList.push(nameData, durationEstimateData, codeData, colorData, idTaskTypeGroupData)

        return _.assign(taskType, {
            customFormatString: null,
            idObject: taskType.idTaskType,
            dataList,
        })
    }

    // OpTastType End


    // Options

    public static getCreateEditOptions(userSession: IUserSession, idTaskType: number = 0, taskType: OpTaskType = null): CreateEditInitParams {
        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: TaskTypeHelper.REFERENCE_TYPE,
            moduleName: TaskTypeHelper.SIMAX_MODULE_NAME,
            sections: null,
            useBusinessObjects: true,

            referenceObject: TaskTypeHelper.createIOpObjectFromOpTaskType(taskType),
            referenceObjectId: idTaskType,

            customDataTypes: {
                paramsRequired: [Enums.DataType.HELPER_NAME, Enums.DataType.HELPER_ID_TASK_TYPE_GROUP],
                paramsToEdit: [Enums.DataType.HELPER_NAME, Enums.DataType.HELPER_DURATION_ESTIMATE, Enums.DataType.HELPER_CODE, Enums.DataType.HELPER_COLOR, Enums.DataType.HELPER_ID_TASK_TYPE_GROUP]
            }
        }
    }

    public static getDetailsOptions(taskTypeId: number, pageItemsIds: number[]): DetailsOptions {
        return {
            hasPermission: true,
            idReferenceType: TaskTypeHelper.REFERENCE_TYPE,
            moduleName: TaskTypeHelper.SIMAX_MODULE_NAME,
            pageItemsIds: pageItemsIds,
            referenceObjectId: taskTypeId,
        }
    }

    public static getMainGridColumns(): ColumnModel[] {
        const nameColumn: ColumnModel = new ColumnModel()
        const durationEstimateColumn: ColumnModel = new ColumnModel()
        const codeColumn: ColumnModel = new ColumnModel()
        const colorColumn: ColumnModel = new ColumnModel()
        const taskCountColumn: ColumnModel = new ColumnModel()

        nameColumn.idName = TaskTypeHelper.NAME_DV_KEY.toLowerCase()
        nameColumn.name = TaskTypeHelper.NAME_DV_KEY
        nameColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)
        nameColumn.type = Enums.DataTypeClass.Text

        durationEstimateColumn.idName = TaskTypeHelper.DURATION_ESTIMATE_DV_KEY.toLowerCase()
        durationEstimateColumn.name = TaskTypeHelper.DURATION_ESTIMATE_DV_KEY
        durationEstimateColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DurationEstimate)
        durationEstimateColumn.type = Enums.DataTypeClass.Integer

        codeColumn.idName = TaskTypeHelper.CODE_DV_KEY.toLowerCase()
        codeColumn.name = TaskTypeHelper.CODE_DV_KEY
        codeColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Code)
        codeColumn.type = Enums.DataTypeClass.Text

        colorColumn.idName = TaskTypeHelper.COLOR_DV_KEY.toLowerCase()
        colorColumn.name = TaskTypeHelper.COLOR_DV_KEY
        colorColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Color)
        colorColumn.type = Enums.DataTypeClass.Text

        taskCountColumn.idName = TaskTypeHelper.TASK_COUNT_DV_KEY.toLowerCase()
        taskCountColumn.name = TaskTypeHelper.TASK_COUNT_DV_KEY
        taskCountColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TaskCount)
        taskCountColumn.type = Enums.DataTypeClass.Integer

        return [nameColumn, durationEstimateColumn, codeColumn, colorColumn, taskCountColumn]
    }

    public static getParmetersFields(taskType: OpTaskType): FieldModel[] {
        const nameFieldModel: FieldModel = new FieldModel()
        const durationEstimateFieldModel: FieldModel = new FieldModel()
        const codeFieldModel: FieldModel = new FieldModel()
        const colorFieldModel: FieldModel = new FieldModel()
        const taskCountFieldModel: FieldModel = new FieldModel()

        const nameDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.NAME_DV_KEY))
        const durationEstimateDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.DURATION_ESTIMATE_DV_KEY))
        const codeDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.CODE_DV_KEY))
        const colorDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.COLOR_DV_KEY))
        const taskCountDynamicValue = _.find(taskType.dynamicValues, ((dynamicValue: any) => dynamicValue.Key === TaskTypeHelper.TASK_COUNT_DV_KEY))

        nameFieldModel.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)
        nameFieldModel.typeId = Enums.DataTypeClass.Text

        durationEstimateFieldModel.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DurationEstimate)
        durationEstimateFieldModel.typeId = Enums.DataTypeClass.Text

        codeFieldModel.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Code)
        codeFieldModel.typeId = Enums.DataTypeClass.Text

        colorFieldModel.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Color)
        colorFieldModel.typeId = Enums.DataTypeClass.Text

        taskCountFieldModel.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TaskCount)
        taskCountFieldModel.typeId = Enums.DataTypeClass.Integer

        if (nameDynamicValue) {
            nameFieldModel.value = nameDynamicValue.Value
        }

        if (durationEstimateDynamicValue) {
            durationEstimateFieldModel.value = durationEstimateDynamicValue.Value
        }

        if (codeDynamicValue) {
            codeFieldModel.value = codeDynamicValue.Value
        }

        if (colorDynamicValue) {
            colorFieldModel.value = colorDynamicValue.Value
        }

        if (taskCountDynamicValue) {
            taskCountFieldModel.value = taskCountDynamicValue.Value
        }

        return [nameFieldModel, durationEstimateFieldModel, codeFieldModel, colorFieldModel, taskCountFieldModel]
    }


    // Options End
}